import { StyledFilterButton, StyledFilterText } from './SharedFilterComponents';
import { usePopoverOnButtonClick } from './filterHooks';
import {
    Box,
    FormControl,
    Input,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    Popover,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { KeyboardArrowDown } from '@mui/icons-material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import UseDebounceInput from '../../../shared/DebounceInput';

export default function DropdownFilter({
    id,
    options,
    handleChange,
    selectedValue,
    isTopLevel = true,
    backgroundColor,
    border,
    minHeight,
    maxHeight,
    disabled = false,
    buttonSx,
}: {
    id?: any;
    options?: any;
    handleChange?: any;
    selectedValue?: any;
    isTopLevel?: boolean;
    backgroundColor?: string;
    border?: string;
    minHeight?: string;
    maxHeight?: string;
    disabled?: boolean;
    buttonSx?: SxProps<Theme>;
}) {
    const { handleClick, handleTab, handleClose, open, popoverId, anchorEl } =
        usePopoverOnButtonClick();

    const [searchText, setSearchText] = UseDebounceInput(10);
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [filteredBySearchTextOptions, setFilteredBySearchTextOptions] =
        useState<any[]>([]);

    useEffect(() => {
        setFilteredBySearchTextOptions(
            options.filter((option: any) => {
                if (
                    option.label
                        ?.toLowerCase()
                        ?.replace(/\s/g, '')
                        ?.includes(
                            searchText?.toLowerCase()?.replace(/\s/g, ''),
                        )
                ) {
                    return true;
                } else {
                    return false;
                }
            }),
        );
    }, [options, searchText]);

    const handleKey = (e: any, value: any) => {
        if (e.key === 'Enter' && !value.disabled) {
            handleChange(value);
            handleClose();
        }
    };

    const theme = useTheme();

    return (
        <FormControl fullWidth>
            <StyledFilterButton
                disableElevation
                // disableRipple
                aria-describedby={popoverId}
                onClick={handleClick}
                // onSelect={handleClick}
                // onKeyDown={handleTab}
                onFocus={handleTab}
                // onMouseEnter={handleClick}
                variant={'outlined'}
                device={isMobile.toString()}
                onChange={handleChange}
                disabled={disabled}
                sx={{
                    backgroundColor: open
                        ? theme.palette.dark.contrastText
                        : backgroundColor,
                    borderColor: open ? theme.palette.info.main : border,
                    color: open
                        ? theme.palette.textAccent.main
                        : theme.typography.body1.color,
                    minHeight: minHeight,
                    maxHeight: maxHeight,
                    ...(minHeight && maxHeight
                        ? {
                              height: maxHeight,
                              marginBottom: 0,
                          }
                        : {}),
                    ...buttonSx,
                }}
            >
                <StyledFilterText variant={'body3'}>
                    {selectedValue === true
                        ? 'Yes'
                        : selectedValue === false
                          ? 'No'
                          : selectedValue}
                </StyledFilterText>
                <Box width={10} flexGrow={1} />
                {isTopLevel ? (
                    <KeyboardArrowDown />
                ) : (
                    <KeyboardArrowRightIcon />
                )}
            </StyledFilterButton>
            <Popover
                id={popoverId}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                disableRestoreFocus
                anchorOrigin={{
                    vertical: isTopLevel ? 'bottom' : 'top',
                    horizontal: isTopLevel ? 'left' : 'right',
                }}
            >
                <List sx={{ maxHeight: '200px', overflow: 'auto' }} id={id}>
                    {options?.length > 2 ? (
                        <ListItem>
                            <Input
                                defaultValue={searchText}
                                placeholder={'Search'}
                                endAdornment={
                                    <InputAdornment position={'end'}>
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </ListItem>
                    ) : null}
                    {filteredBySearchTextOptions.map(
                        (option: {
                            value: any;
                            label: any;
                            disabled: boolean;
                        }) => (
                            <ListItem
                                onKeyDown={(e: any) => {
                                    if (!option.disabled) {
                                        handleKey(e, option.value);
                                    }
                                }}
                                onClick={() => {
                                    if (!option.disabled) {
                                        handleChange(option.value);
                                        handleClose();
                                    }
                                }}
                                key={option.value}
                                value={option.value}
                                sx={{
                                    color: option.disabled
                                        ? theme.palette.dark.contrastText
                                        : theme.typography.body1.color,
                                    '&:hover': {
                                        cursor: 'pointer',
                                        backgroundColor:
                                            theme.palette.dark.contrastText,
                                    },
                                }}
                            >
                                <ListItemButton
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    {' '}
                                    {option.label}
                                </ListItemButton>
                            </ListItem>
                        ),
                    )}
                </List>
            </Popover>
        </FormControl>
    );
}
